import { Box, Text, Button, Container, Image, Flex } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProjectById } from '../data/projectsData';
import ContentBlock from '../components/ContentBlock';
import { ChevronLeft } from 'lucide-react';
import theme from '../themes/theme';

export default function ProjectDetail() {
    // Get the project ID from the URL
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    
    // Find the project data using the ID
    const project = projectId ? getProjectById(projectId) : undefined;

    // Function to navigate back and scroll to projects section
    const handleBackClick = () => {
        navigate('/');
        // Use setTimeout to ensure navigation completes before scrolling
        setTimeout(() => {
            const projectsSection = document.getElementById('portfolio');
            if (projectsSection) {
                projectsSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    // If project doesn't exist, show error
    if (!project) {
        return (
            <Box
                minH="100vh"
                backgroundColor="black.200"
                display="flex"
                alignItems="center"
                justifyContent="center"
                padding="5%"
            >
                <Box textAlign="center">
                    <Text fontSize="4xl" color="white.200" mb={4}>
                        Project Not Found
                    </Text>
                    <Button
                        onClick={handleBackClick}
                        colorScheme="green"
                    >
                        Back to Home
                    </Button>
                </Box>
            </Box>
        );
    }

    /**
     * Render a nice looking back button
     */
    function BackButton() {
        // Back Button
        return(
            <Flex
                w={"fit-content"}
                h={"50px"}
                alignItems={"center"}
                gap={2}
                cursor="pointer"
                onClick={handleBackClick}
                role="button"
                _hover={{
                    '& > div:first-of-type': {
                        transform: 'translateX(-4px)'
                    }
                }}
            >
                {/* Chevron Icon */}
                <Box
                    transition="transform 0.3s ease"
                >
                    <ChevronLeft color={theme.colors.white[300]} />
                </Box>

                {/* Text */}
                <Box
                    fontFamily={"Source Code Pro Variable"}
                    color={theme.colors.white[300]}
                    position="relative"
                    _after={{
                        content: '""',
                        position: 'absolute',
                        bottom: '-2px',
                        left: 0,
                        width: '0%',
                        height: '1px',
                        backgroundColor: theme.colors.white[300],
                        transition: 'width 0.3s ease',
                    }}
                    sx={{
                        transform: 'skewX(0deg)',
                        transition: 'transform 0.3s ease',
                    }}
                    _hover={{
                        transform: 'skewX(-15deg)',
                        _after: {
                            width: '100%',
                        }
                    }}
                >
                    Back to Portfolio
                </Box>
            </Flex>
        )
    }

    return (
        <Box
            minH="100vh"
            backgroundColor="black.200"
            paddingTop="120px"
            paddingBottom="80px"
            width="100%"
            maxW="100vw"
            overflowX="hidden"
        >
            <Container maxW="container.xl" px={{ base: 5, md: 6 }} width="100%">

                {/* Back Button */}
                <BackButton />

                {/* Hero Section */}
                <Box mb={12} width="100%">
                    <Text
                        fontSize={{ base: '2xl', sm: '3xl', md: '6xl' }}
                        fontFamily="Source Code Pro Variable"
                        fontWeight="600"
                        color="white.200"
                        mb={4}
                        wordBreak="break-word"
                        overflowWrap="break-word"
                        width="100%"
                    >
                        {project.title}
                    </Text>

                    <Flex
                        py={1}
                        mb={4}
                        borderRadius="md"
                        fontSize="lg"
                        width={"fit-content"}
                        fontFamily={"Source Code Pro Variable"}
                    >

                        {/* Status */}
                        <Text color="white.100">Status: </Text>
                        <Text
                            fontWeight="600"
                            marginLeft={1}
                            color={{
                                "In Progress": "yellow.400",
                                "Live": "green.400",
                                "Archived": "gray.400",
                                "Beta": "blue.100"
                            }[project.status]}
                        >
                            {project.status}
                        </Text>

                        {/* Bubble Icon with Pulse Effect */}
                        <Box
                            ml={2}
                            alignSelf="center"
                            position="relative"
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            width="10px"
                            height="10px"
                        >
                            {/* Ripple Effect */}
                            <Box
                                position="absolute"
                                top="50%"
                                left="50%"
                                width="10px"
                                height="10px"
                                borderRadius="50%"
                                border="2.5px solid"
                                borderColor={{
                                    "In Progress": "transparent",
                                    "Live": "green.400",
                                    "Archived": "transparent",
                                    "Beta": "blue.100"
                                }[project.status]}
                                animation="pulse 1.5s ease-out infinite"
                                sx={{
                                    '@keyframes pulse': {
                                        '0%': {
                                            opacity: 1,
                                            transform: 'translate(-50%, -50%) scale(1)',
                                        },
                                        '100%': {
                                            opacity: 0,
                                            transform: 'translate(-50%, -50%) scale(3)',
                                        },
                                    },
                                }}
                            />
                            {/* Dot */}
                            <Box
                                width="10px"
                                height="10px"
                                borderRadius="50%"
                                backgroundColor={{
                                    "In Progress": "yellow.400",
                                    "Live": "green.400",
                                    "Archived": "gray.400",
                                    "Beta": "blue.100"
                                }[project.status]}
                                position="relative"
                                zIndex={1}
                                boxShadow={{
                                    "In Progress": "transparent",
                                    "Live": "green.400",
                                    "Archived": "transparent",
                                    "Beta": "blue.100"
                                }[project.status]}
                                sx={{
                                    color: {
                                        "In Progress": "yellow.400",
                                        "Live": "green.400",
                                        "Archived": "gray.400",
                                        "Beta": "blue.100"
                                    }[project.status]
                                }}
                            />
                        </Box>

                    </Flex>

                    <Text
                        fontSize={{ base: 'md', md: 'xl' }}
                        color="gray.300"
                        mb={6}
                        width="100%"
                    >
                        {project.description}
                    </Text>

                    <Flex gap={2} wrap="wrap" mb={8} width="100%">
                        {project.tags.map((tag, index) => (
                            <Box
                                key={index}
                                px={3}
                                py={1}
                                bg="green.500"
                                borderRadius="md"
                                fontSize="sm"
                                color="white"
                                flexShrink={0}
                            >
                                {tag}
                            </Box>
                        ))}
                    </Flex>

                    {project.link && (
                        <Button
                            as="a"
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            fontFamily={"Source Code Pro Variable"}
                            fontWeight={"600"}
                            fontSize={{ base: "lg", md: "2xl" }}
                            color={"black.200"}
                            backgroundColor={"blue.300"}
                            borderRadius={0}
                            width={"100%"}
                            maxW="100%"
                            height={{ base: "60px", md: "80px" }}
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            mb={8}
                        >
                            View Project
                        </Button>
                    )}
                    
                    {/* Hero Image */}
                    <Box width="100%" maxW="100%">
                        <Image
                            src={project.image}
                            alt={project.title}
                            borderRadius="xl"
                            width="100%"
                            maxW="100%"
                            objectFit="cover"
                            maxH="500px"
                        />
                    </Box>
                </Box>

                {/* Content Blocks */}
                {project.content && project.content.length > 0 ? (
                    <Box>
                        <Box maxW="800px" mx="auto" width="100%" mb={"50px"}>
                            {project.content.map((block, index) => (
                                <ContentBlock key={index} block={block} />
                            ))}
                        </Box>
                        <BackButton />
                    </Box>
                ) : (
                    <Box textAlign="center" py={12} width="100%">
                        <Text fontSize="xl" color="gray.400">
                            More details coming soon...
                        </Text>
                    </Box>
                )}
            </Container>
        </Box>
    );
}